import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Gavel, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ItemKeyLink, UserChip } from '@/components/item-bits';
import { Badge, Button, Card, Dialog, EmptyState, Field, PageLoader, Tabs, Textarea } from '@/components/ui';
import { errorMessage, http } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { NarrativeButton } from '@/features/ai/AiTools';
import { useAiStatus } from '@/lib/queries';
import { useUi } from '@/lib/ui-store';
import { ago, cn } from '@/lib/utils';

export interface Approval {
  id: string;
  kind: 'transition' | 'go_no_go';
  title: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  requestedBy: string;
  approverIds: string[];
  decidedBy: string | null;
  decidedAt: string | null;
  comment: string | null;
  requestComment: string | null;
  subjectId: string;
  payload: Record<string, unknown>;
  snapshot: GoNoGoSnapshot | null;
  createdAt: string;
  subject: { key?: string; title?: string; releaseName?: string; buildLabel?: string; releaseId?: string; buildId?: string } | null;
}

interface GoNoGoSnapshot {
  takenAt: string;
  build: { label: string; state: string };
  items: { total: number; done: number };
  openBugs: number;
  openCritical: { key: string; title: string; severity: string | null }[];
  carriedBugs: { key: string; title: string }[];
  documentation?: { complete: boolean; required: { docType: string; state: string }[] } | null;
  testing: { overall: { effectivePassRate: number | null; progress: number; criticalPassRate: number | null; failed: number } | null; readiness: { plan: string; readiness: string }[] } | null;
}

const STATUS: Record<string, string> = {
  pending: 'bg-warning/15 text-warning',
  approved: 'bg-success/15 text-success',
  rejected: 'bg-danger/10 text-danger',
  cancelled: 'bg-muted text-subtle',
};
const pct = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v * 1000) / 10}%`);

function Snapshot({ s }: { s: GoNoGoSnapshot }) {
  const o = s.testing?.overall;
  return (
    <div className="grid gap-2 rounded-md bg-muted/50 p-2 text-xs md:grid-cols-2">
      <div className="space-y-0.5">
        <div>
          Items done: <b>{s.items.done}</b>/{s.items.total} · open bugs <b>{s.openBugs}</b>
        </div>
        {o && (
          <div>
            Effective pass <b>{pct(o.effectivePassRate)}</b> · critical {pct(o.criticalPassRate)} · progress {pct(o.progress)} · {o.failed} failed
          </div>
        )}
        {s.testing?.readiness.map((r) => (
          <div key={r.plan}>
            {r.plan}: <Badge className={r.readiness === 'ready' ? STATUS.approved : STATUS.pending}>{r.readiness.replace('_', ' ')}</Badge>
          </div>
        ))}
        {!s.testing && <div className="text-subtle">Test management is off for this project.</div>}
      </div>
      <div className="space-y-0.5">
        {!!s.openCritical.length && (
          <div>
            <span className="font-medium text-danger">Open critical:</span>{' '}
            {s.openCritical.map((i) => (
              <ItemKeyLink key={i.key} itemKey={i.key} className="mr-1" />
            ))}
          </div>
        )}
        {!!s.carriedBugs.length && (
          <div>
            Carried:{' '}
            {s.carriedBugs.map((i) => (
              <ItemKeyLink key={i.key} itemKey={i.key} className="mr-1" />
            ))}
          </div>
        )}
        {s.documentation && (
          <div>
            Documentation:{' '}
            {s.documentation.required.map((d) => (
              <Badge key={d.docType} className={d.state === 'complete' || d.state === 'waived' ? STATUS.approved : STATUS.rejected}>
                {d.docType.replace(/_/g, ' ').toLowerCase()} {d.state}
              </Badge>
            ))}
          </div>
        )}
        <div className="text-subtle">Snapshot {ago(s.takenAt)}</div>
      </div>
    </div>
  );
}

/** One approval with decide/cancel actions for the right people (FR-WF-7). */
export function ApprovalCard({ a, onChanged }: { a: Approval; onChanged?: () => void }) {
  const qc = useQueryClient();
  const me = useAuth((s) => s.user);
  const [rejecting, setRejecting] = useState(false);
  const { data: ai } = useAiStatus();
  const [comment, setComment] = useState('');
  const act = useMutation({
    mutationFn: ({ action, comment }: { action: 'approve' | 'reject' | 'cancel'; comment?: string }) => http.post(`/approvals/${a.id}/${action}`, action === 'cancel' ? undefined : { comment: comment || undefined }),
    onSuccess: (_r, v) => {
      toast.success(v.action === 'approve' ? 'Approved' : v.action === 'reject' ? 'Rejected' : 'Cancelled');
      setRejecting(false);
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['item'] });
      void qc.invalidateQueries({ queryKey: ['release'] });
      onChanged?.();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const canDecide = a.status === 'pending' && a.requestedBy !== me?.id && (a.approverIds.includes(me?.id ?? '') || me?.orgRole === 'org_admin');
  const canCancel = a.status === 'pending' && (a.requestedBy === me?.id || me?.orgRole === 'org_admin');
  return (
    <Card className="space-y-2 p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Gavel className="h-4 w-4 text-subtle" />
        <span className="font-medium">{a.title}</span>
        <Badge className={STATUS[a.status]}>{a.status}</Badge>
        {a.subject?.key && <ItemKeyLink itemKey={a.subject.key} />}
        {a.subject?.releaseId && (
          <Link to={`/releases/${a.subject.releaseId}`} className="text-xs text-brand hover:underline">
            {a.subject.releaseName}
          </Link>
        )}
        <span className="ml-auto flex items-center gap-1 text-xs text-subtle">
          <UserChip userId={a.requestedBy} size={16} /> asked {ago(a.createdAt)}
        </span>
      </div>
      {a.requestComment && <div className="text-sm text-subtle">“{a.requestComment}”</div>}
      {a.snapshot && <Snapshot s={a.snapshot} />}
      <div className="flex flex-wrap items-center gap-2 text-xs text-subtle">
        {a.status === 'pending' ? (
          <span className="flex items-center gap-1">
            Approvers:{' '}
            {a.approverIds.map((u) => (
              <UserChip key={u} userId={u} size={16} />
            ))}
          </span>
        ) : (
          <span className="flex items-center gap-1">
            {a.status} by <UserChip userId={a.decidedBy} size={16} /> {ago(a.decidedAt)} {a.comment && `· “${a.comment}”`}
          </span>
        )}
        <div className="ml-auto flex gap-2">
          {a.kind === 'go_no_go' && a.snapshot && ai?.enabled && ai.features.release_narrative !== false && <NarrativeButton url={`/ai/approvals/${a.id}/summary`} label="AI summary" />}
          {canCancel && (
            <Button size="sm" variant="ghost" onClick={() => act.mutate({ action: 'cancel' })}>
              Cancel request
            </Button>
          )}
          {canDecide && (
            <>
              <Button size="sm" variant="outline" onClick={() => setRejecting(true)}>
                <X className="h-3.5 w-3.5" /> Reject
              </Button>
              <Button size="sm" onClick={() => act.mutate({ action: 'approve' })} loading={act.isPending}>
                <Check className="h-3.5 w-3.5" /> Approve
              </Button>
            </>
          )}
        </div>
      </div>
      {rejecting && (
        <Dialog
          open
          onOpenChange={setRejecting}
          title={`Reject: ${a.title}`}
          footer={
            <Button onClick={() => act.mutate({ action: 'reject', comment })} disabled={!comment.trim()} loading={act.isPending}>
              Reject
            </Button>
          }
        >
          <Field label="Reason (kept on the permanent record)">
            <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} autoFocus />
          </Field>
        </Dialog>
      )}
    </Card>
  );
}

export const usePendingApprovals = () => useQuery({ queryKey: ['approvals', 'inbox'], queryFn: () => http.get<Approval[]>('/approvals'), refetchInterval: 60_000 });

/** Approvals inbox (FR-WF-7): decisions waiting for me, and my own requests. */
export default function ApprovalsPage() {
  const { setPageContext } = useUi();
  useEffect(() => setPageContext({ route: '/approvals' }), [setPageContext]);
  const [tab, setTab] = useState('inbox');
  const inbox = usePendingApprovals();
  const mine = useQuery({ queryKey: ['approvals', 'mine'], queryFn: () => http.get<Approval[]>('/approvals/mine'), enabled: tab === 'mine' });
  const list = tab === 'inbox' ? inbox : mine;
  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4 md:p-6">
      <h1 className="text-xl font-semibold">Approvals</h1>
      <Tabs
        value={tab}
        onValueChange={setTab}
        tabs={[
          { value: 'inbox', label: `To decide${inbox.data?.length ? ` (${inbox.data.length})` : ''}` },
          { value: 'mine', label: 'My requests' },
        ]}
      />
      {list.isLoading && <PageLoader />}
      {list.data && !list.data.length && <EmptyState icon={<Gavel className="h-8 w-8" />} title={tab === 'inbox' ? 'Nothing waiting for your decision' : 'You have no requests'} />}
      <div className={cn('space-y-3')}>
        {list.data?.map((a) => (
          <ApprovalCard key={a.id} a={a} />
        ))}
      </div>
    </div>
  );
}
